import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Dónde enviar a un visitante sin sesión. Default `/auth` (pareja); el área
   * de proveedor usa `/vendor-auth`.
   */
  redirectTo?: string;
}

/**
 * Guarda de ruta reutilizable (007 · TV-3). Centraliza la protección de acceso
 * que hoy está copiada por página con comportamientos inconsistentes (algunas
 * navegan durante el render → flash de contenido). Redirige de forma
 * declarativa (`<Navigate>`), sin flash: mientras `loading` muestra un spinner;
 * sin sesión redirige; con sesión renderiza el contenido.
 *
 * El refinamiento por rol (vendor/admin) permanece en su página destino
 * (`BackofficeDashboard`, `AdminDashboard`), que ya lo resuelve; esta guarda
 * cubre el gate de *sesión* común a todas las áreas autenticadas.
 */
export function ProtectedRoute({ children, redirectTo = '/auth' }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
